import { useState } from 'react';
import { useNavigate } from 'react-router';
import { formatInteger } from '@brimo/shared';
import { Banner, Button, Eyebrow, Pill } from '../../../ds/components';
import { errorMessage } from '../../../lib/api';
import { requestAccess } from '../../../shell/access';
import type { PipelineInfo } from '../types';
import styles from './board.module.css';

/**
 * Deal-Pipeline 13 „Kein Recht · fremde Pipeline": the locked shape of `GET /deals/pipelines/:id`
 * (responsible, deputy, property count) with „Zugriff beantragen" (access approval for the deals tool)
 * and „Zu meiner Pipeline".
 */
export function LockedPipeline({ info }: { info: Extract<PipelineInfo, { locked: true }> }) {
  const navigate = useNavigate();
  const [state, setState] = useState<'idle' | 'pending' | 'done' | 'error'>('idle');
  const [error, setError] = useState<string | null>(null);

  async function request() {
    setState('pending');
    setError(null);
    try {
      await requestAccess({ kind: 'tool', tool: 'deals' });
      setState('done');
    } catch (err) {
      setError(errorMessage(err));
      setState('error');
    }
  }

  return (
    <div className={styles.lockedCard} role="region" aria-label={info.name}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <span className={styles.lockedTitle}>{info.name}</span>
        <Pill variant="neutral">Pipeline</Pill>
      </div>
      <div className={styles.lockedTitle} style={{ marginTop: 18, fontSize: 'var(--text-lg)' }}>
        {info.title}
      </div>
      <p className={styles.lockedText}>{info.text}</p>
      <div className={styles.lockedFacts}>
        <div className={styles.lockedFact}>
          <Eyebrow>Verantwortlich</Eyebrow>
          <span className={styles.lockedFactValue}>{info.responsible?.name ?? '—'}</span>
        </div>
        <div className={styles.lockedFact}>
          <Eyebrow>Vertretung</Eyebrow>
          <span className={styles.lockedFactValue}>{info.deputy?.name ?? '—'}</span>
        </div>
        <div className={styles.lockedFact}>
          <Eyebrow>Objekte</Eyebrow>
          <span className={styles.lockedFactValue}>{formatInteger(info.propertyCount)}</span>
        </div>
      </div>
      {error ? (
        <div style={{ marginTop: 16 }}>
          <Banner tone="danger">{error}</Banner>
        </div>
      ) : null}
      {state === 'done' ? (
        <div style={{ marginTop: 16 }}>
          <Banner tone="success">Ihr Antrag wurde gesendet.</Banner>
        </div>
      ) : null}
      <div className={styles.lockedActions}>
        <Button
          variant="dark"
          loading={state === 'pending'}
          disabled={state === 'done'}
          onClick={() => void request()}
        >
          Zugriff beantragen
        </Button>
        <Button variant="secondary" onClick={() => void navigate('/tasks')}>
          Zu meiner Pipeline
        </Button>
      </div>
    </div>
  );
}
